'use client'

import type { RenderLink } from '@ui/web/utils'
import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import { ContactAdminButton } from '../../contact-admin-button'

interface EmptyProps {
  type: 'web' | 'school'
  renderLink?: RenderLink
  /** Member is not assigned to any class — show a distinct message and no create CTA. */
  noClass?: boolean
  /** Organization has no children at all — show a "register students" message instead of "no activity yet". */
  noStudents?: boolean
  /** First name of the signed-in user, used to personalise the no-students greeting. */
  userName?: string
  /** Currently selected school class — links the "Kelola rombel" CTA to that class's student page. */
  teamId?: string | null
}

export function Empty({ type, renderLink, noClass, noStudents, userName, teamId }: EmptyProps) {
  const createStudentButton = renderLink ? (
    <Button asChild>{renderLink({ children: `Buat akun ${type === 'web' ? 'anak' : 'murid'}` })}</Button>
  ) : null

  const manageClassButton = renderLink ? (
    <Button asChild>{renderLink({ children: 'Kelola rombel', meta: teamId ? { teamId } : undefined })}</Button>
  ) : null

  return (
    <section className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:h-fit srd:my-10 srd:text-center srd:text-neutral-black'>
      {(() => {
        // State 2 — member is not assigned to any class. No students to manage, so no create CTA.
        if (noClass) {
          return (
            <>
              <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>
                Kamu belum ditugaskan ke rombel mana pun
              </h6>

              <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
                Hubungi admin sekolah untuk mendapatkan akses ke kelas dan mulai memantau aktivitas murid.
              </p>

              {/*
                Makes the sentence above actionable instead of an instruction the teacher has to
                carry out somewhere else. School only: on the family app the reader IS the owner of
                their organization, so there is no admin to ask. The button gates itself on role and
                class count, so it stays hidden for anyone this copy does not apply to.
              */}
              {type === 'school' && <ContactAdminButton className='srd:mt-2 srd:w-fit' />}
            </>
          )
        }

        // State 1 — the organization has no students at all. Prompt to register students.
        if (noStudents) {
          return (
            <>
              <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>
                Halo{userName ? `, ${userName}` : ''}! Siap memulai?
              </h6>

              <img
                src={getStaticAssetUrl('empty-tab-image/empty-tab-lesson.png')}
                alt=''
                width={400}
                height={400}
                draggable={false}
              />

              <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
                Pantau aktivitas membaca dan belajar {type === 'web' ? 'anak' : 'murid'} di satu tempat. Temukan siapa
                yang perlu dorongan, dan rayakan setiap kemajuan.
              </p>

              {createStudentButton}
            </>
          )
        }

        // State 3 — member is in a class, but the class has no students yet.
        return (
          <>
            <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>Belum ada murid di rombel kelas ini</h6>

            <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
              Tambahkan murid ke dalam rombel kelas ini dan lihat siapa yang konsisten, siapa yang perlu dorongan, dan
              rayakan pencapaian mingguan bersama.
            </p>

            {manageClassButton}
          </>
        )
      })()}
    </section>
  )
}
